import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { type Dispatch } from 'redux';

import { type Clip } from '@air/api/types';
import { hydrateSelectedItemsAction, type SelectableItem } from '@air/redux-selected-items';

import { resolveSelectableItemsForClipIdsAsync } from '~/components/v2/components/workspace/workspace-agent-panel/utils/resolveWorkspaceAgentClipIdsScope';
import { useViewsSelectAllStore } from '~/components/v2/store/features/views/useViewsSelectAllStore';
import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';
import { useAirStore } from '~/utils/ReduxUtils';

export type HydrateWorkspaceSelectionForClipIdsResult = {
  hasFullClipSelection: boolean;
};

const hydrateWorkspaceSelectionForClipIds = (
  dispatch: Dispatch,
  clipIds: Clip['id'][],
  selectableItems: SelectableItem[],
): HydrateWorkspaceSelectionForClipIdsResult => {
  if (selectableItems.length === 0) {
    return { hasFullClipSelection: false };
  }

  const resolvedIds = new Set(selectableItems.map((item) => item.id));
  const hasFullClipSelection = clipIds.length > 0 && clipIds.every((clipId) => resolvedIds.has(clipId));

  if (!hasFullClipSelection) {
    return { hasFullClipSelection: false };
  }

  const { resetSelectAll, selectAll } = useViewsSelectAllStore.getState();

  if (selectAll) {
    resetSelectAll();
  }

  dispatch(hydrateSelectedItemsAction({ selectedItems: selectableItems }));

  return { hasFullClipSelection: true };
};

export const useHydrateWorkspaceSelectionForClipIds = () => {
  const dispatch = useDispatch();
  const store = useAirStore();
  const queryClient = useQueryClient();
  const { currentWorkspace } = useCurrentWorkspace();
  const currentWorkspaceIdRef = useRef(currentWorkspace?.id);

  currentWorkspaceIdRef.current = currentWorkspace?.id;

  return useCallback(
    async (clipIds: Clip['id'][], launchWorkspaceId?: string): Promise<HydrateWorkspaceSelectionForClipIdsResult> => {
      const expectedWorkspaceId = launchWorkspaceId ?? currentWorkspaceIdRef.current;

      const selectableItems = await resolveSelectableItemsForClipIdsAsync({
        clipIds,
        queryClient,
        state: store.getState(),
        workspaceId: expectedWorkspaceId,
      });

      if (expectedWorkspaceId && currentWorkspaceIdRef.current !== expectedWorkspaceId) {
        return { hasFullClipSelection: false };
      }

      return hydrateWorkspaceSelectionForClipIds(dispatch, clipIds, selectableItems);
    },
    [dispatch, queryClient, store],
  );
};
